import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@features/ui';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@lib/utils';
import { ChevronRight } from 'lucide-react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';

import { BaseNavItem } from './types';

type Props = {
  item: BaseNavItem;
};

export const NavItemWithSub: React.FC<Props> = ({ item }) => {
  const { setOpenMobile } = useSidebar();
  const { t } = useTranslation();
  const location = useLocation();

  // Check if any sub-item is active
  const hasActiveSubItem = item.items?.some((subItem) =>
    checkIsActive(location.pathname, subItem.url)
  );

  const [isOpen, setIsOpen] = useState(hasActiveSubItem || false);

  const isParentActive = item.url
    ? checkIsActive(location.pathname, item.url)
    : hasActiveSubItem;

  return (
    <CollapsiblePrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <SidebarMenuItem>
        <div className="flex gap-x-5 pr-2 md:pr-0">
          <div
            className={cn(
              'w-1 rounded-tr-[4px] rounded-br-[4px]',
              isParentActive ? 'bg-primary' : 'bg-transparent'
            )}
          />
          <CollapsiblePrimitive.Trigger asChild>
            <SidebarMenuButton
              className="w-48 h-[50px] gap-x-[18px]"
              tooltip={item.title}
              variant={isParentActive ? 'gradient' : 'default'}
            >
              {item.icon && <item.icon />}
              <span>{t(item.title)}</span>
              <ChevronRight
                className={cn(
                  'ml-auto transition-transform duration-200',
                  isOpen && 'rotate-90'
                )}
              />
            </SidebarMenuButton>
          </CollapsiblePrimitive.Trigger>
        </div>
        <CollapsiblePrimitive.Content className="p-2">
          <SidebarMenuSub>
            {item.items?.map((subItem) => {
              const isSubActive = checkIsActive(location.pathname, subItem.url);

              return (
                <SidebarMenuSubItem key={subItem.url}>
                  <SidebarMenuSubButton asChild isActive={isSubActive}>
                    <Link to={subItem.url} onClick={() => setOpenMobile(false)}>
                      <span>{t(subItem.title)}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsiblePrimitive.Content>
      </SidebarMenuItem>
    </CollapsiblePrimitive.Root>
  );
};

function checkIsActive(href: string, url: string) {
  if (url === '/') {
    return href.split('?')[0] === url;
  }

  return href.includes(url) || href.split('?')[0] === url;
}
